"use client";

import React, { useRef } from "react";
import { useGSAP } from "@gsap/react";
import hasFadeAnim from "@/lib/animation/hasFadeAnim";
import hasTextMovAnim from "@/lib/animation/hasTextMovAnim";
import gsap from "gsap";

const COLS = 3;

type Value = {
  title: string;
  description: string;
};

type Props = {
  title?: string;
  values?: Value[];
};
/**
 * Hitung posisi dot (%) untuk satu baris desktop.
 */
function getDotPositions(rowCount: number, isCentered: boolean): number[] {
  const colOffset = isCentered ? (COLS - rowCount) / 2 : 0;
  return Array.from(
    { length: rowCount },
    (_, i) => ((colOffset + i + 0.5) / COLS) * 100,
  );
}

// Potong array values menjadi baris-baris berisi max COLS item
function chunkRows<T>(arr: T[], size: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    rows.push(arr.slice(i, i + size));
  }
  return rows;
}

// Divider desktop — dots posisi dinamis
const DesktopDivider = ({ dotPositions }: { dotPositions: number[] }) => (
  <div className="divider-line-container relative w-full h-[2px] my-4 md:my-6 overflow-visible">
    {/* Running Line */}
    <div className="divider-progress absolute top-0 left-0 h-full bg-black/10 w-0" />

    {dotPositions.map((pos, i) => (
      <div
        key={i}
        className="divider-dot absolute w-[12px] h-[12px] rounded-full bg-black opacity-0 scale-0"
        style={{
          left: `${pos}%`,
          top: "50%",
          transform: "translate(-50%, -50%)",
        }}
      />
    ))}
  </div>
);

const ValueCard = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => (
  <div className="value-card opacity-0 translate-y-6 flex flex-col items-center text-center px-4 md:px-8">
    <h3 className="text-[20px] sm:text-[22px] lg:text-[24px] font-bold mb-3 leading-tight tracking-tight text-black">
      {title}
    </h3>
    <p className="text-[16px] sm:text-[18px] lg:text-[20px] leading-relaxed text-black/70 font-light w-full">
      {description}
    </p>
  </div>
);

const OurValues = ({ title = "Our Values", values = [] }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null!);

  useGSAP(
    () => {
      hasFadeAnim();
      hasTextMovAnim();

      // --- Desktop Animation ---
      const rows = gsap.utils.toArray(".value-row");
      if (rows.length > 0) {
        const masterTl = gsap.timeline({
          scrollTrigger: {
            trigger: containerRef.current.querySelector(".hidden.sm\\:block"),
            start: "top 85%",
            toggleActions: "play none none none",
          },
        });

        rows.forEach((row: any, rowIdx: number) => {
          const progress = row.querySelector(".divider-progress");
          const dots = row.querySelectorAll(".divider-dot");
          const cards = row.querySelectorAll(".value-card");

          const rowDuration = 1.2;
          const rowStartTime = rowIdx * (rowDuration + 0.2); // Small gap between rows

          // Running line animation
          masterTl.to(
            progress,
            {
              width: "100%",
              duration: rowDuration,
              ease: "power1.inOut",
            },
            rowStartTime,
          );

          // Dots and Cards reveal
          dots.forEach((dot: any, i: number) => {
            const posPercent = parseFloat(dot.style.left);
            const relativeStartTime = (posPercent / 100) * rowDuration;
            const absoluteStartTime = rowStartTime + relativeStartTime;

            masterTl.to(
              dot,
              {
                opacity: 1,
                scale: 1,
                duration: 0.4,
                ease: "back.out(2)",
              },
              absoluteStartTime,
            );

            if (cards[i]) {
              masterTl.to(
                cards[i],
                {
                  opacity: 1,
                  y: 0,
                  duration: 0.6,
                  ease: "power2.out",
                },
                absoluteStartTime + 0.1,
              );
            }
          });
        });
      }

      // --- Mobile Animation ---
      const mobileItems = gsap.utils.toArray(".mobile-value-item");
      mobileItems.forEach((item: any) => {
        const dot = item.querySelector(".mobile-dot");
        const line = item.querySelector(".mobile-line");
        const content = item.querySelector(".mobile-content");

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: item,
            start: "top 90%",
            toggleActions: "play none none none",
          },
        });

        tl.to(dot, { scale: 1, opacity: 1, duration: 0.4, ease: "back.out(2)" })
          .to(
            content,
            { opacity: 1, x: 0, duration: 0.6, ease: "power2.out" },
            "-=0.2",
          )
          .to(line, { scaleY: 1, duration: 0.6, ease: "none" }, "-=0.4");
      });
    },
    { scope: containerRef },
  );
  const COLS = 3;
  const rows = chunkRows(values, COLS);

  return (
    <section
      className="w-full bg-white text-black section-spacing-top"
      ref={containerRef}
    >
      <div className="inner-container-2">
        {/* Title */}
        <h2 className="has_text_mov_anim text-center text-[28px] md:text-[36px] lg:text-[42px] font-bold leading-tight tracking-tight mb-10 md:mb-14">
          {title}
        </h2>

        {/* ─── MOBILE: vertical timeline ───────────────────────── */}
        <div className="sm:hidden">
          {/* Tidak pakai gap, pb ada di dalam content div supaya gutter bisa extend ke bawah */}
          <div className="flex flex-col px-8">
            {values.map((v, i) => {
              const isFirst = i === 0;
              const isLast = i === values.length - 1;

              return (
                <div
                  key={v.title}
                  className="mobile-value-item flex items-start gap-5"
                >
                  {/* Gutter: dot di atas, garis mengisi sisa + extend ke gap berikutnya */}
                  <div className="self-stretch flex flex-col items-center flex-shrink-0 w-[12px]">
                    {/* Dot — mt-2 supaya sejajar tengah baris pertama title */}
                    <div className="mobile-dot opacity-0 scale-0 flex-shrink-0 mt-2 w-[10px] h-[10px] rounded-full bg-black z-10" />
                    {/* Garis bawah — extend -8px ke bawah agar terhubung ke dot berikutnya */}
                    <div
                      className={`mobile-line flex-1 w-[2px] origin-top scale-y-0 ${isLast ? "bg-transparent" : "bg-black/20"}`}
                      style={{ marginBottom: isLast ? 0 : "-8px" }}
                    />
                  </div>

                  {/* Konten — pb menentukan jarak antar item */}
                  <div
                    className={`mobile-content opacity-0 translate-x-4 flex flex-col ${isLast ? "pb-0" : "pb-10"}`}
                  >
                    <h3 className="text-[20px] font-bold mb-2 tracking-tight text-black leading-tight">
                      {v.title}
                    </h3>
                    <p className="text-[16px] leading-relaxed text-black/70 font-light ml-4">
                      {v.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── TABLET & DESKTOP: grid per baris ───────────────────── */}
        <div className="hidden sm:block">
          {rows.map((row, rowIdx) => {
            const isFull = row.length === COLS;
            const isCentered = !isFull;
            const dotPositions = getDotPositions(row.length, isCentered);

            return (
              <div key={rowIdx} className="value-row">
                <DesktopDivider dotPositions={dotPositions} />
                <div
                  className={`${rowIdx < rows.length - 1 ? "pb-10 md:pb-16" : ""} ${
                    isCentered
                      ? "flex justify-center"
                      : "grid grid-cols-3 gap-4"
                  }`}
                >
                  {row.map((v) => (
                    <div
                      key={v.title}
                      className={isCentered ? "w-1/3" : undefined}
                    >
                      <ValueCard {...v} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OurValues;
